import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  useDisclosure,
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Avatar,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Divider
} from "@chakra-ui/react";
import {
  Card, CardHeader, Right, CardContent, CardTitle
} from "../../../../components/Card";
import CreateCustomerModal from "../CustomerCreate";
import urlToId from "../../../../helpers/UrlToId";
import Link from "../../../../components/Link";
import { AddIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FiUsers, FiStar, FiTrendingUp, FiShield, FiPhone, FiMapPin, FiFilter, FiMoreVertical } from "react-icons/fi";
import { MdAutoAwesome, MdDiamond, MdVerified } from "react-icons/md";
import { BsStars, BsGem } from "react-icons/bs";
import PhoneNumber from "../../../../components/buttons/PhoneNumber";

import CustomerVerified from "../../../../components/CustomerVerifiedToolTip";
import ExportButton from "../../../../components/export/ExportButton";
import { useTranslation } from 'react-i18next';
import Pagination from "../../../../components/pagination/Pagination";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export default function CustomerListTableView({ customers }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const createCustomerModalDisclosure = useDisclosure();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [sortField, setSortField] = useState("user");
  const [sortDirection, setSortDirection] = useState("asc");
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("phone_verified") === "true"
      ? "verified"
      : searchParams.get("phone_verified") === "false"
        ? "unverified"
        : "all"
  );
  const initialItems = Array.isArray(customers) ? customers : (customers?.results || []);
  const [items, setItems] = useState(initialItems);
  const [currentPage, setCurrentPage] = useState(Array.isArray(customers) ? 1 : (customers?.current_page || 1));
  const [numOfPages, setNumOfPages] = useState(Array.isArray(customers) ? 1 : (customers?.num_of_pages || 1));
  const [pageSize, setPageSize] = useState(Array.isArray(customers) ? customers.length : (customers?.page_size || 20));
  const [totalItems, setTotalItems] = useState(Array.isArray(customers) ? customers.length : (customers?.num_of_items || initialItems.length));

  useEffect(() => {
    const nextItems = Array.isArray(customers) ? customers : (customers?.results || []);
    setItems(nextItems);
    setCurrentPage(Array.isArray(customers) ? 1 : (customers?.current_page || 1));
    setNumOfPages(Array.isArray(customers) ? 1 : (customers?.num_of_pages || 1));
    setPageSize(Array.isArray(customers) ? customers.length : (customers?.page_size || 20));
    setTotalItems(Array.isArray(customers) ? customers.length : (customers?.num_of_items || nextItems.length));
  }, [customers]);

  useEffect(() => {
    const q = searchParams.get("search") || "";
    const pv = searchParams.get("phone_verified");
    const status = pv === "true" ? "verified" : pv === "false" ? "unverified" : "all";
    setSearchTerm(q);
    setStatusFilter(status);
  }, [searchParams]);

  // Filtrelenmiş ve sıralanmış müşteri listesi
  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = items.slice();

    // Sıralama
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case "user":
          aValue = `${a.user.first_name} ${a.user.last_name}`.toLowerCase();
          bValue = `${b.user.first_name} ${b.user.last_name}`.toLowerCase();
          break;
        case "city_name":
          aValue = a.city_name?.toLowerCase() || "";
          bValue = b.city_name?.toLowerCase() || "";
          break;
        case "phone_number":
          aValue = a.phone_number || "";
          bValue = b.phone_number || "";
          break;
        case "phone_verified":
          aValue = a.phone_verified ? 1 : 0;
          bValue = b.phone_verified ? 1 : 0;
          break;
        default:
          aValue = a[sortField];
          bValue = b[sortField];
      }
      
      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  }, [items, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const applyFilters = (overrides = {}) => {
    const params = new URLSearchParams(location.search);
    const nextSearch = overrides.search ?? searchTerm;
    const nextStatus = overrides.status ?? statusFilter;
    if (nextSearch && nextSearch.trim()) params.set("search", nextSearch.trim());
    else params.delete("search");
    if (nextStatus === "verified") params.set("phone_verified", "true");
    else if (nextStatus === "unverified") params.set("phone_verified", "false");
    else params.delete("phone_verified");
    if (!("page" in overrides)) params.delete("page");
    const qs = params.toString();
    navigate({ pathname: "/musteriler/", search: qs ? `?${qs}` : "" });
  };

  const handleSearchSubmit = () => applyFilters({ page: 1 });
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    applyFilters({ status: value, page: 1 });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const columns = [
    {
      title: t('customer.customer'),
      dataIndex: "user",
      sortable: true,
      render: (text, record) => (
        <HStack spacing={3}>
          <Avatar
            size="sm"
            bg="blue.500"
            color="white"
            fontSize="sm"
          >
          </Avatar>
          <VStack align="start" spacing={0}>
            <Link to={`/musteriler/${urlToId(record.url)}`}>
              <Text fontWeight="semibold" color="blue.600" _hover={{ color: "blue.800" }}>
                {`${text.first_name} ${text.last_name}`}
              </Text>
            </Link>
            <Text fontSize="xs" color="gray.500">
              ID: {urlToId(record.url)}
            </Text>
          </VStack>
        </HStack>
      ),
    },
    {
      title: t('customer.contact'),
      dataIndex: "contact",
      sortable: false,
      render: (text, record) => (
        <VStack align="start" spacing={1}>
          <HStack spacing={2}>
            <FiPhone size={12} color="gray.500" />
            <PhoneNumber number={record.phone_number} />
          </HStack>
          {record.city_name && (
            <HStack spacing={2}>
              <FiMapPin size={12} color="gray.500" />
              <Text fontSize="sm" color="gray.600">{record.city_name}</Text>
            </HStack>
          )}
        </VStack>
      ),
    },
    {
      title: t('customer.reseller'),
      dataIndex: "last_reseller",
      sortable: false,
      render: (text) => (
        <Box>
          {text ? (
            <Badge colorScheme="green" variant="subtle" fontSize="xs">
              {text.name}
            </Badge>
          ) : (
            <Text fontSize="sm" color="gray.400">{t('customer.unassigned')}</Text>
          )}
        </Box>
      ),
    },
    {
      title: t('customer.status'),
      dataIndex: "phone_verified",
      sortable: true,
      render: (text) => (
        <HStack spacing={2}>
          <CustomerVerified state={text} />
          {text && <MdVerified color="green" size={16} />}
        </HStack>
      )
    },
    {
      title: t('customer.actions'),
      dataIndex: "actions",
      sortable: false,
      render: (text, record) => (
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<FiMoreVertical />}
            variant="ghost"
            size="sm"
            _hover={{ bg: "gray.100" }}
          />
          <MenuList>
            <MenuItem as={Link} to={`/musteriler/${urlToId(record.url)}`}>
              {t('customer.view_details')}
            </MenuItem>
            <MenuItem>
              {t('customer.edit')}
            </MenuItem>
            <Divider />
            <MenuItem color="red.500">
              {t('customer.delete')}
            </MenuItem>
          </MenuList>
        </Menu>
      ),
    },
  ];

  const bgGradient = "linear(135deg, white 0%, blue.50 100%)";
    const headerBg = "linear(135deg, red.600 0%, red.900 100%)";

  const cardBg = "linear(135deg, red.600 0%, red.900 100%)";
  const tableBg = "gray.50";
  const borderColor = "gray.300";
  const hoverBg = "gray.50";
  const headerHoverBg = "gray.100";
  const alternateRowBg = "gray.25";

  const exportCustomersData = useMemo(() => {
    const list = Array.isArray(filteredAndSortedCustomers) ? filteredAndSortedCustomers : [];
    return list.map((c) => ({
      [t('customer.name')]: `${c.user?.first_name || ''} ${c.user?.last_name || ''}`.trim(),
      [t('customer.phone')]: c.phone_number || "",
      [t('customer.city')]: c.city_name || "",
      [t('customer.verified')]: c.phone_verified ? t('common.open') : t('common.closed'),
    }));
  }, [filteredAndSortedCustomers]);

  const customerExportColumns = useMemo(() => ([
    { key: t('customer.name'), label: t('customer.name') },
    { key: t('customer.phone'), label: t('customer.phone') },
    { key: t('customer.city'), label: t('customer.city') },
    { key: t('customer.verified'), label: t('customer.verified') },
  ]), [t]);

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      p={6}
    >
      <CreateCustomerModal disclosure={createCustomerModalDisclosure} />
      
      {/* Header */}
      <Box
         bgGradient={headerBg}
        borderRadius="xl"
        p={6}
        mb={6}
        shadow="lg"
        border="1px"
        borderColor={borderColor}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <HStack spacing={4}>
            <Box p={3} bg="blue.50" borderRadius="lg">
              <FiUsers size={24} color="#3182CE" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="2xl" fontWeight="bold"  color="white">
                {t('customer.customer_management')}
              </Text>
              <Text fontSize="sm" color="white">
                {filteredAndSortedCustomers.length} / {totalItems} {t('customer.customers_displayed')}
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={3}>
            <ExportButton
              data={exportCustomersData}
              filename="customers"
              columns={customerExportColumns}
              sheetName={t('customer.customers')}
            />
            <Button
              onClick={createCustomerModalDisclosure.onOpen}
              leftIcon={<AddIcon />}
              
              size="md"
              shadow="md"
              _hover={{ transform: "translateY(-1px)", shadow: "lg" }}
            >
              {t('customer.new_customer')}
            </Button>
          </HStack>
        </Flex>
        
        {/* Filters */}
        <Flex gap={4} wrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder={t('customer.search_customer')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchSubmit(); }}
              bg="white"
              border="1px"
              borderColor="gray.300"
              _focus={{ borderColor: "blue.500", shadow: "0 0 0 1px #3182CE" }}
            />
          </InputGroup>
          
          <Select
            maxW="200px"
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            bg="white"
            border="1px"
            borderColor="gray.300"
          >
            <option value="all">{t('customer.all_statuses')}</option>
            <option value="verified">{t('customer.verified')}</option>
            <option value="unverified">{t('customer.unverified')}</option>
          </Select>
          
          <Tooltip label={t('filter')}>
            <IconButton
              icon={<FiFilter />}
              variant="outline"
              colorScheme="gray"
              bg="white"
              onClick={handleSearchSubmit}
            />
          </Tooltip>
        </Flex>
      </Box>
      {/* Content Area */}
      <Box
        bg={cardBg}
        borderRadius="xl"
        shadow="lg"
        border="1px"
        borderColor={borderColor}
        overflow="hidden"
      >
        <TableContainer>
          <Table size="md">
            <Thead bg={tableBg}>
              <Tr>
                {columns.map((column) => (
                  <Th
                    key={column.dataIndex}
                    py={4}
                    px={6}
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color="gray.600"
                    borderColor={borderColor}
                    cursor={column.sortable ? "pointer" : "default"}
                    _hover={column.sortable ? { bg: headerHoverBg } : {}}
                    onClick={() => column.sortable && handleSort(column.dataIndex)}
                  >
                    <Flex align="center">
                      {column.title}
                      {column.sortable && getSortIcon(column.dataIndex)}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {filteredAndSortedCustomers.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} py={12} textAlign="center">
                    <VStack spacing={4}>
                      <Box p={4} bg="gray.50" borderRadius="full">
                        <FiUsers size={32} color="gray.400" />
                      </Box>
                      <VStack spacing={2}>
                        <Text fontSize="lg" fontWeight="semibold" color="gray.600">
                          {searchTerm || statusFilter !== "all" ? t('customer.no_customers_found') : t('customer.no_customers_yet')}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {searchTerm || statusFilter !== "all" ? t('customer.try_different_search') : t('customer.add_first_customer_hint')}
                        </Text>
                      </VStack>
                      {(!searchTerm && statusFilter === "all") && (
                        <Button
                          onClick={createCustomerModalDisclosure.onOpen}
                          leftIcon={<AddIcon />}
                          colorScheme="blue"
                          variant="outline"
                        >
                          {t('customer.add_first_customer')}
                        </Button>
                      )}
                    </VStack>
                  </Td>
                </Tr>
              ) : (
                filteredAndSortedCustomers.map((customer, index) => (
                  <Tr
                    key={customer.url}
                    _hover={{
                      bg: hoverBg,
                      transform: "translateY(-1px)",
                      shadow: "md"
                    }}
                    transition="all 0.2s ease"
                    bg={index % 2 === 0 ? "transparent" : alternateRowBg}
                  >
                    {columns.map((column) => (
                      <Td
                        key={column.dataIndex}
                        py={4}
                        px={6}
                        borderColor={borderColor}
                        verticalAlign="middle"
                      >
                        {column.render ? (
                          column.render(customer[column.dataIndex], customer)
                        ) : (
                          <Text>{customer[column.dataIndex]}</Text>
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
      <Box mt={4}>
        <Pagination
          path={"musteriler"}
          num_of_items={totalItems}
          num_of_pages={numOfPages}
          currentPage={currentPage}
          onPageChange={(p) => {
            const params = new URLSearchParams(location.search);
            if (p && p > 1) params.set("page", String(p));
            else params.delete("page");
            const qs = params.toString();
            navigate({ pathname: "/musteriler/", search: qs ? `?${qs}` : "" });
          }}
        />
      </Box>
    </Box>
  );
}
